import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '@mui/material'
import { getGridStringOperators, getGridNumericOperators, getGridDateOperators } from '@mui/x-data-grid'
import AppDataGrid from '../../components/global/AppDataGrid'
import ConditionValues from './components/ConditionValues'
import PriceSelectionActions from './components/PriceSelectionActions'
import PricePrimaryActions from './components/PricePrimaryActions'
import PriceDeletionDialog from './components/PriceDeletionDialog'
import PriceDetailsDialog from './components/PriceDetailsDialog'
import RefreshInfoDialog from './components/RefreshInfoDialog'
import PricePaginationRange from './components/PricePaginationRange'
import PriceRowAction from './components/PriceRowAction'
import DirectSapPriceDialog from './components/DirectSapPriceDialog'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import { apiUrl } from '../../environments/environments'
import './index.css'
import { SAP_DIRECT_BUILDERS as BUILDERS, useSapDirectNavigation } from './Navigation'

const PRICE_FILTER_OPERATORS = {
    string: getGridStringOperators(),
    number: getGridNumericOperators(),
    dateTime: getGridDateOperators(true),
}

export const hasActivePriceFilters = (filterModel) => filterModel.items.some((item) => {
    const type = item.field === 'totalPrice' ? 'number' : item.field === 'updatedAt' ? 'dateTime' : 'string'
    const operator = PRICE_FILTER_OPERATORS[type].find((candidate) => candidate.value === item.operator)
    return operator?.requiresFilterValue === false ||
        (Array.isArray(item.value) ? item.value.length > 0 : item.value !== undefined && item.value !== null && item.value !== '')
})

export const buildPricesQuery = (paginationModel, filterModel, sortModel) => ({
    limit: paginationModel.pageSize,
    skip: paginationModel.page * paginationModel.pageSize,
    filterModel: JSON.stringify({
        ...filterModel,
        items: filterModel.items.map((item) => {
            if (item.field !== 'updatedAt' || !item.value) return item
            const date = new Date(item.value)
            return Number.isNaN(date.getTime()) ? item : { ...item, value: date.toISOString() }
        }),
    }),
    sortModel: JSON.stringify(sortModel),
})

const getRefreshConfirmation = (builderLabel, total, filterModel) => {
    const filtered = hasActivePriceFilters(filterModel)
    return {
        title: `Confirm ${builderLabel} price refresh`,
        message: (filtered
            ? `Refresh ${builderLabel} prices matching the active filters?`
            : `No active filters. Refresh all ${builderLabel} prices?`) + ' Other builders will not be refreshed.',
        confirmDisabled: total === 0,
    }
}

export const getPriceErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.error
    return (typeof detail === 'string' ? detail : detail?.message) || fallback
}

export const getDeletionConfirmation = (deletion, builderLabel) => {
    const count = deletion?.count || 0
    const prices = count === 1 ? 'cached price' : 'cached prices'
    const title = `Delete ${count} ${builderLabel} ${prices}?`
    if (deletion?.row) {
        return {
            title,
            message: `Single entry: ${deletion.row.kmat}; catalogs: ${[deletion.row.catalog, deletion.row.catalog2, deletion.row.catalog3].map((value) => value || '(empty)').join(' / ')}.`,
        }
    }
    if (deletion?.body.mode === 'allMatching') {
        const excluded = deletion.body.excludedIds.length
        return {
            title,
            message: (deletion.filtered
                ? 'Delete entries matching the current filters across all pages.'
                : `Delete entries across all pages for ${builderLabel}.`) +
                (excluded ? ` ${excluded} unchecked ${excluded === 1 ? 'entry is' : 'entries are'} excluded.` : ''),
        }
    }
    return {
        title,
        message: count === 1
            ? 'Only the selected entry will be deleted.'
            : `Only the ${count} selected entries will be deleted, including selections on other pages.`,
    }
}

export const buildDeletionSelection = (selection, total, filterModel) => selection.allMatching
    ? { mode: 'allMatching', filterModel: JSON.parse(filterModel), excludedIds: selection.excludedIds, expectedCount: total - selection.excludedIds.length }
    : { mode: 'ids', ids: selection.ids, expectedCount: selection.ids.length }

export const getContextualRefresh = (selection, total, filterModel, serializedFilter, builderLabel) => {
    const count = selection.allMatching ? Math.max(0, total - selection.excludedIds.length) : selection.ids.length
    if (count > 0) {
        return {
            label: 'Refresh selected prices',
            body: { selection: buildDeletionSelection(selection, total, serializedFilter) },
            progressSuffix: ` (${count} selected prices)`,
            confirmation: {
                title: `Confirm ${builderLabel} selected price refresh`,
                message: `Refresh only the ${count} selected ${builderLabel} prices${selection.allMatching ? ' across all matching pages, excluding unchecked entries' : ', including selected entries on other pages'}? Unselected prices and other builders will not be refreshed.`,
            },
        }
    }
    return {
        label: hasActivePriceFilters(filterModel) ? 'Refresh filtered prices' : 'Refresh all prices',
        body: { filterModel: serializedFilter },
        progressSuffix: '',
        confirmation: getRefreshConfirmation(builderLabel, total, filterModel),
    }
}

export const mergePriceHeaderFilters = (model, values) => {
    const items = Object.entries(values).filter(([, value]) => value !== '').map(([field, value]) => ({
        id: `header-${field}`,
        field,
        operator: field === 'totalPrice' ? '=' : field === 'updatedAt' ? 'is' : 'contains',
        value,
    }))
    return items.length ? {
        ...model,
        logicOperator: 'and',
        items: [...model.items.filter((item) => !items.some((header) => header.field === item.field)), ...items],
    } : model
}
const emptySelection = () => ({ ids: [], allMatching: false, excludedIds: [] })
const isPriceRowSelectable = ({ row }) => !row.__isErrorRow

const priceFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const formatPrice = (value) => value == null ? '' : priceFormatter.format(value)

export const getPriceRowHeight = ({ model }) => Math.max(52, (model.conditions?.length || 0) * 28 + 16)

function SapDirectPricesPage({ match }) {
    const routeBuilder = match?.params?.builder || window.location.pathname.split('/').pop()
    const builder = BUILDERS.some((item) => item.key === routeBuilder) ? routeBuilder : 'fancoils'
    return <SapDirectPricesTable key={builder} builder={builder} />
}

function SapDirectPricesTable({ builder }) {
    const { setDisabled: setNavigationDisabled } = useSapDirectNavigation()
    const builderLabel = BUILDERS.find((item) => item.key === builder).label
    const directPricesUrl = `${apiUrl}/SAPDirect/${builder}/prices`
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [listingFailed, setListingFailed] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const [directOpen, setDirectOpen] = useState(false)
    const [refreshInfoOpen, setRefreshInfoOpen] = useState(false)
    const [refreshingRowId, setRefreshingRowId] = useState(null)
    const [rowErrors, setRowErrors] = useState({})
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
    const [gridFilterModel, setGridFilterModel] = useState({ items: [] })
    const [headerFilters, setHeaderFilters] = useState({})
    // Community DataGrid supports one native filter; header searches are combined on the server.
    const filterModel = useMemo(() => mergePriceHeaderFilters(gridFilterModel, headerFilters), [gridFilterModel, headerFilters])
    const [sortModel, setSortModel] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [selection, setSelection] = useState(emptySelection)
    const [deletion, setDeletion] = useState(null)
    const deletionConfirmation = getDeletionConfirmation(deletion, builderLabel)
    const [deleting, setDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    const [selectionStale, setSelectionStale] = useState(false)
    const [notice, setNotice] = useState('')
    const [syncing, setSyncing] = useState(false)
    const [refreshConfirmationOpen, setRefreshConfirmationOpen] = useState(false)
    const deletionLock = useRef(false)
    const requestSequence = useRef(0)
    const frozen = Boolean(deletion) || deleting || refreshConfirmationOpen || syncing
    const mutationBusy = loading || refreshingRowId !== null || frozen
    const selectedCount = selection.allMatching ? Math.max(0, rowCount - selection.excludedIds.length) : selection.ids.length
    const selectionModel = useMemo(() => selection.allMatching
        ? rows.filter((row) => !selection.excludedIds.includes(row.id)).map((row) => row.id)
        : selection.ids, [selection, rows])
    const pageSelected = rows.length > 0 && rows.every((row) => selectionModel.includes(row.id))
    const refresh = getContextualRefresh(selection, rowCount, filterModel, buildPricesQuery(paginationModel, filterModel, sortModel).filterModel, builderLabel)

    useEffect(() => {
        setNavigationDisabled(frozen)
        return () => setNavigationDisabled(false)
    }, [frozen, setNavigationDisabled])

    useEffect(() => {
        loadRows()
        return () => { requestSequence.current += 1 }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [builder, paginationModel, filterModel, sortModel])

    const loadRows = async () => {
        const sequence = ++requestSequence.current
        setLoading(true)
        setError('')
        try {
            const { data } = await ApiService(directPricesUrl, 'get', null, null, 0, buildPricesQuery(paginationModel, filterModel, sortModel))
            if (sequence !== requestSequence.current) return
            if (!Array.isArray(data?.entries) || !Number.isSafeInteger(data.total) || data.total < 0) {
                throw new Error('Invalid SAP Direct price listing response.')
            }
            setRows(data.entries)
            setListingFailed(false)
            setRowCount(data.total)
            const lastPage = Math.max(0, Math.ceil(data.total / paginationModel.pageSize) - 1)
            if (paginationModel.page > lastPage) {
                setPaginationModel((previous) => ({ ...previous, page: lastPage }))
            }
            return data
        } catch (err) {
            if (sequence !== requestSequence.current) return
            setRows([])
            setListingFailed(true)
            setError(getPriceErrorMessage(err, err.message || 'Unable to load SAP Direct prices.'))
        } finally {
            if (sequence === requestSequence.current) setLoading(false)
        }
    }

    const changeSelection = (ids) => {
        if (mutationBusy || deletionLock.current) return
        setNotice('')
        setSelection((previous) => {
            if (!previous.allMatching) {
                const nextIds = ids.filter((id) => Number.isInteger(id))
                if (nextIds.length === previous.ids.length && nextIds.every((id) => previous.ids.includes(id))) return previous
                return { ...previous, ids: nextIds }
            }
            const excluded = new Set(previous.excludedIds)
            rows.forEach((row) => {
                if (ids.includes(row.id)) excluded.delete(row.id)
                else excluded.add(row.id)
            })
            if (excluded.size === previous.excludedIds.length && previous.excludedIds.every((id) => excluded.has(id))) return previous
            return { ...previous, excludedIds: [...excluded] }
        })
    }

    const requestDeletion = async (row = null) => {
        if (mutationBusy || deletionLock.current) return
        deletionLock.current = true
        setNotice('')
        try {
            const latest = await loadRows()
            if (!latest) return
            const body = row
                ? { mode: 'ids', ids: [row.id], expectedCount: 1 }
                : buildDeletionSelection(selection, latest.total, buildPricesQuery(paginationModel, filterModel, sortModel).filterModel)
            if (!row && body.expectedCount < 1) {
                setError('No selected prices remain. Clear the selection and select prices again.')
                return
            }
            setDeleteError('')
            setSelectionStale(false)
            setDeletion({
                row,
                body,
                count: row ? 1 : body.expectedCount,
                filtered: hasActivePriceFilters(filterModel),
                filterModel: JSON.parse(buildPricesQuery(paginationModel, filterModel, sortModel).filterModel),
            })
        } finally {
            deletionLock.current = false
        }
    }

    const confirmDeletion = async () => {
        if (!deletion || deletionLock.current || selectionStale) return
        deletionLock.current = true
        setDeleting(true)
        setDeleteError('')
        try {
            const { data } = await ApiService(directPricesUrl, 'delete', deletion.body, null, 0)
            if (!Number.isInteger(data?.deletedCount) || data.deletedCount !== deletion.count) {
                setSelectionStale(true)
                throw new Error('Unexpected deletion result. Reload the table before deleting again.')
            }
            setSelection(emptySelection())
            setRowErrors({})
            setSelectedRow(null)
            setDeletion(null)
            setNotice(`${data.deletedCount} cached price${data.deletedCount === 1 ? '' : 's'} deleted.`)
            await loadRows()
        } catch (err) {
            setDeleteError(getPriceErrorMessage(err, err.message || 'Unable to delete SAP Direct prices.'))
            if (err.response?.status === 409 || err.response?.status === 404) setSelectionStale(true)
        } finally {
            deletionLock.current = false
            setDeleting(false)
        }
    }

    const clearRowError = (rowId) => {
        setRowErrors((prev) => {
            if (!(rowId in prev)) return prev
            const next = { ...prev }
            delete next[rowId]
            return next
        })
    }

    const refreshRow = async (row) => {
        setRefreshingRowId(row.id)
        clearRowError(row.id)
        try {
            await ApiService(`${directPricesUrl}/${row.id}/refresh`, 'post', {}, null, 0)
            await loadRows()
        } catch (err) {
            const message = getPriceErrorMessage(err, `Unable to refresh ${row.kmat} (${row.catalog}) from SAP.`)
            setRowErrors((prev) => ({ ...prev, [row.id]: message }))
        } finally {
            setRefreshingRowId(null)
        }
    }

    const openRow = async (row) => {
        try {
            const { data } = await ApiService(`${directPricesUrl}/${row.id}`, 'get', null, null, 0)
            setSelectedRow(data)
            clearRowError(row.id)
        } catch (err) {
            setRowErrors((prev) => ({ ...prev, [row.id]: getPriceErrorMessage(err, 'Unable to load price details.') }))
        }
    }

    const columns = [
        {
            field: 'kmat',
            headerName: 'KMAT',
            flex: 1.1,
            minWidth: 150,
            renderCell: ({ row, value }) =>
                row.__isErrorRow ? <span className='sap-direct-price-error-cell'>{row.__errorMessage}</span> : value,
        },
        {
            field: 'varcond', headerName: 'Varcond', flex: 1.2, minWidth: 180, sortable: false,
            renderCell: ({ row }) => <ConditionValues conditions={row.conditions} field='varcond' />,
        },
        {
            field: 'description', headerName: 'Description', flex: 1.5, minWidth: 220, sortable: false,
            renderCell: ({ row }) => <ConditionValues conditions={row.conditions} field='description' />,
        },
        { field: 'language', headerName: 'Language', minWidth: 110, flex: 0.6, sortable: false },
        { field: 'catalog', headerName: 'Catalog', flex: 1, minWidth: 140 },
        { field: 'catalog2', headerName: 'Catalog 2', flex: 1, minWidth: 120 },
        { field: 'catalog3', headerName: 'Catalog 3', flex: 1, minWidth: 120 },
        {
            field: 'totalPrice', headerName: 'Total price', type: 'number', flex: 0.8, minWidth: 130,
            valueGetter: ({ value }) => (value == null ? null : Number(value)),
            valueFormatter: ({ value }) => formatPrice(value),
        },
        {
            field: 'updatedAt',
            headerName: 'Last update',
            type: 'dateTime',
            flex: 1.2,
            minWidth: 180,
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 96,
            getActions: ({ row }) => row.__isErrorRow ? [] : [
                <PriceRowAction
                    key='refresh'
                    action='refresh'
                    row={row}
                    refreshing={refreshingRowId === row.id}
                    disabled={mutationBusy}
                    onClick={refreshRow}
                />,
                <PriceRowAction
                    key='delete'
                    action='delete'
                    row={row}
                    disabled={mutationBusy}
                    onClick={requestDeletion}
                />,
            ],
        },
    ]

    // Keep errors on a dedicated line spanning the price and action columns.
    columns[0].colSpan = (params) => (params.row.__isErrorRow ? columns.length : undefined)

    const displayRows = rows.flatMap((row) => {
        const message = rowErrors[row.id]
        if (!message) return [row]
        return [row, { id: `error-${row.id}`, __isErrorRow: true, __errorMessage: message }]
    })


    return (
        <div className='page-container sap-direct-prices-page'>
            <section className='sap-direct-price-table-section'>
                {error && <Alert severity='error'>{error}</Alert>}
                {notice && <Alert severity='success' onClose={() => setNotice('')}>{notice}</Alert>}
                <fieldset disabled={frozen || deletionLock.current} className='sap-direct-price-grid'>
                    <AppDataGrid
                        headerFilters={{
                            values: headerFilters,
                            disabled: frozen || refreshingRowId !== null,
                            onChange: (field, value) => {
                                if (frozen || deletionLock.current || refreshingRowId !== null) return
                                setHeaderFilters((previous) => ({ ...previous, [field]: value }))
                                setGridFilterModel((previous) => ({ ...previous, items: previous.items.filter((item) => item.field !== field) }))
                                setSelection(emptySelection())
                                setPaginationModel((previous) => ({ ...previous, page: 0 }))
                            },
                        }}
                        showToolbar
                        toolbarLeft={
                            <PriceSelectionActions
                                count={selectedCount}
                                allMatching={selection.allMatching}
                                total={rowCount}
                                canSelectAll={!selection.allMatching && pageSelected && rowCount > rows.length}
                                disabled={mutationBusy}
                                onDelete={() => requestDeletion()}
                                onClear={() => setSelection(emptySelection())}
                                onSelectAll={() => setSelection({ ids: [], allMatching: true, excludedIds: [] })}
                            />
                        }
                        toolbarRight={
                                <PricePrimaryActions
                                    builder={builder}
                                    refresh={refresh}
                                    pricesUrl={directPricesUrl}
                                    disabled={mutationBusy}
                                    refreshDisabled={mutationBusy || listingFailed || rowCount === 0}
                                    onQuery={() => setDirectOpen(true)}
                                    onInfo={() => setRefreshInfoOpen(true)}
                                    onSyncComplete={() => {
                                        setRowErrors({})
                                        setSelectedRow(null)
                                        if (refresh.body.selection) setNotice(`${refresh.body.selection.expectedCount} selected prices refreshed.`)
                                        loadRows()
                                    }}
                                    onSyncStateChange={setSyncing}
                                    onConfirmationStateChange={setRefreshConfirmationOpen}
                                    onError={(message) => setError(message)}
                                />
                        }
                        rows={displayRows}
                        getRowHeight={getPriceRowHeight}
                        columns={columns}
                        loading={loading}
                        checkboxSelection
                        checkboxSelectionVisibleOnly
                        keepNonExistentRowsSelected
                        hideFooterSelectedRowCount
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={changeSelection}
                        isRowSelectable={isPriceRowSelectable}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model) => {
                            if (frozen || deletionLock.current) return
                            setPaginationModel((previous) => ({
                                ...model,
                                page: model.pageSize !== previous.pageSize ? 0 : model.page,
                            }))
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        rowCount={rowCount}
                        slotProps={{
                            baseCheckbox: mutationBusy ? { disabled: true } : {},
                            toolbar: {
                                role: 'toolbar',
                                'aria-label': 'Selected price actions',
                                sx: { bgcolor: 'white' },
                            },
                            pagination: {
                                labelDisplayedRows: (params) => <PricePaginationRange {...params} pageSize={paginationModel.pageSize} />,
                            },
                        }}
                        filterMode='server'
                        filterModel={gridFilterModel}
                        onFilterModelChange={(model) => {
                            if (frozen || deletionLock.current) return
                            setSelection(emptySelection())
                            setGridFilterModel(model)
                            setHeaderFilters((previous) => Object.fromEntries(Object.entries(previous).filter(([field]) => !model.items.some((item) => item.field === field))))
                            setPaginationModel((previous) => ({ ...previous, page: 0 }))
                        }}
                        sortingMode='server'
                        sortModel={sortModel}
                        onSortModelChange={(model) => {
                            if (frozen || deletionLock.current) return
                            setSortModel(model)
                            setPaginationModel((previous) => ({ ...previous, page: 0 }))
                        }}
                        disableRowSelectionOnClick
                        onRowClick={({ row }) => {
                            if (row.__isErrorRow || frozen) return
                            openRow(row)
                        }}
                        autoHeight
                        getRowClassName={({ row }) => (row.__isErrorRow ? 'sap-direct-price-error-row' : '')}
                        localeText={{
                            noRowsLabel: 'No SAP Direct prices match. Use Query SAP to request a price.',
                            checkboxSelectionSelectAllRows: 'Select visible page',
                            checkboxSelectionUnselectAllRows: 'Unselect visible page',
                        }}
                    />
                </fieldset>
            </section>
            <RefreshInfoDialog
                open={refreshInfoOpen}
                onClose={() => setRefreshInfoOpen(false)}
            />
            <PriceDeletionDialog
                deletion={deletion}
                confirmation={deletionConfirmation}
                deleting={deleting}
                selectionStale={selectionStale}
                error={deleteError}
                onClose={() => { if (!deletionLock.current) setDeletion(null) }}
                onReload={() => {
                    setDeletion(null)
                    setSelection(emptySelection())
                    loadRows()
                }}
                onConfirm={confirmDeletion}
            />
            <PriceDetailsDialog
                row={selectedRow}
                onClose={() => setSelectedRow(null)}
            />
            <DirectSapPriceDialog
                open={directOpen}
                builder={builder}
                onClose={() => setDirectOpen(false)}
                onPersisted={loadRows}
            />
        </div>
    )
}

export default SapDirectPricesPage